import { useMemo } from 'react';
import type { EduRankings } from 'interfaces/EduRanking';
import Tabs from 'components/Globals/Tabs';
import RankingCardList, { type Variant } from 'components/education/EduRanking/RankingCardList';
import { withErrorBoundary } from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import RankingListsTabs from '../RankingListsTabs';
import * as S from './RankingsSectionStyles';

export interface RankingsSectionProps extends EduRankings {
  variant: Variant;
  sectionTitle?: string;
}

const RankingsSection = ({ rankings, categories, variant, sectionTitle }: RankingsSectionProps) => {
  const rankingsByCategory = useMemo(() => {
    if (!rankings || rankings.length === 0 || !categories || categories.length === 0) return null;
    return categories.map((category) => {
      const rankingList = rankings.filter((ranking) => ranking.category === category.slug);
      return {
        name: category.name,
        rankings: rankingList.filter(({ latestList }) => latestList && latestList.length > 0),
        slug: category.slug,
      };
    });
  }, [rankings, categories]);

  if (!rankings || rankings.length === 0) return null;

  return (
    <S.SectionWrapper>
      {sectionTitle && (
        <S.SectionTitle
          $fontFamily='saol'
          $size={{ default: 'level3', xlMin: 'level2' }}
        >
          {sectionTitle}
        </S.SectionTitle>
      )}
      {rankingsByCategory && (
        <Tabs
          enableTabsSlider
          hideArrowOnDesktop
          config={rankingsByCategory.map(({ name, slug, rankings: list }) => ({
            children: (
              <>
                <RankingCardList
                  key={slug}
                  rankings={list.slice(0, 4)}
                  variant={variant}
                />
                {variant === 'education-section-rankings' && (
                  <RankingListsTabs
                    tabs={list
                      .filter((rank) => !rank.hideRanking)
                      ?.map(({ latestList, title, url }) => ({
                        cta: {
                          title: 'SEE FULL RANKING',
                          url,
                        },
                        items: latestList,
                        title,
                      }))}
                  />
                )}
              </>
            ),
            title: name,
          }))}
          defaultIndex={0}
          variant='buttons'
        />
      )}
      {!rankingsByCategory && (
        <RankingCardList
          rankings={rankings}
          variant={variant}
        />
      )}
    </S.SectionWrapper>
  );
};

export default withErrorBoundary(RankingsSection, { fragment: UIFragments.EDU_RANKINGS_SECTION });
